/**
 * Dr. Salus — Control Panel
 * Visão de observabilidade por usuário (phone): lista de lembretes ativos +
 * estado de engajamento + atividade dos últimos 7 dias. Cada linha expande
 * pra mostrar todos os lembretes daquele usuário, com ações rápidas:
 * desativar individual, bulk deactivate do usuário, gerenciar engagement,
 * testar envio.
 *
 * Diferente da RemindersPage (que lista flat por lembrete), esta tela é
 * voltada pro cliente operar e dar manutenção — busca por número, status
 * agregado, e bloqueio com um clique.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, Search, RefreshCw, Loader2, AlertCircle, Check, X,
  ChevronDown, ChevronRight, Bell, BellOff, Send, Trash2,
  Users, Pause, AlertTriangle, CheckCircle2, Power, Copy as CopyIcon,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import drSalusService from '../../services/dr-salus/drSalusService';
import reminderService from '../../services/dr-salus/reminderService';
import templateService from '../../services/global/templateService';

const DAYS = {
  MON: 'Seg', TUE: 'Ter', WED: 'Qua', THU: 'Qui', FRI: 'Sex', SAT: 'Sáb', SUN: 'Dom',
};

function formatSchedule(reminder) {
  const t = reminder.schedule_time || '--:--';
  switch (reminder.schedule_type || 'daily') {
    case 'daily':
      if ((reminder.days_of_week || []).includes('DAILY')) return `Todo dia · ${t}`;
      return `${(reminder.days_of_week || []).map(d => DAYS[d] || d).join(', ')} · ${t}`;
    case 'weekly':
      return `Toda ${(reminder.days_of_week || []).map(d => DAYS[d] || d).join(', ')} · ${t}`;
    case 'monthly':
      return `Dia ${(reminder.days_of_month || []).join(', ')} de cada mês · ${t}`;
    case 'once':
      return `Único: ${reminder.one_shot_date || '?'} · ${t}`;
    default:
      return t;
  }
}

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const min = Math.round(ms / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min}min`;
    const h = Math.round(min / 60);
    if (h < 24) return `há ${h}h`;
    const days = Math.round(h / 24);
    if (days < 7) return `há ${days}d`;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

export default function ControlPanelPage() {
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);  // só o load inicial bloqueia a UI
  const [refreshing, setRefreshing] = useState(false);  // reloads pós-ação mantêm a UI
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [expandedPhone, setExpandedPhone] = useState(null);
  const [engagementSettings, setEngagementSettings] = useState(null);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [engagementForm, setEngagementForm] = useState(null);  // { phone, max, reengage }
  const [savingEng, setSavingEng] = useState(false);
  const [actingOn, setActingOn] = useState(null);  // id of reminder being acted on

  const load = useCallback(async (opts = {}) => {
    const isInitial = !data || opts.initial;
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [panel, settings, tpl] = await Promise.all([
        drSalusService.getControlPanel({ phone: search, includeInactive }),
        engagementSettings ? Promise.resolve(engagementSettings) : drSalusService.getEngagementSettings().catch(() => null),
        approvedTemplates.length ? Promise.resolve({ templates: approvedTemplates }) : templateService.list().catch(() => ({ templates: [] })),
      ]);
      setData(panel);
      if (!engagementSettings && settings) setEngagementSettings(settings);
      if (!approvedTemplates.length && tpl?.templates) {
        setApprovedTemplates(tpl.templates.filter(t => t.status === 'APPROVED'));
      }
    } catch {
      setError('Erro ao carregar painel de controle.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [data, search, includeInactive, engagementSettings, approvedTemplates.length]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeInactive]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const flashSuccess = (m) => {
    setSuccessMsg(m);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // Aplica patch em um lembrete específico dentro do `data` sem precisar
  // refazer a requisição. Cosmos tem consistência eventual entre o write e
  // o próximo read, então o load() pós-ação às vezes traz dados stale —
  // atualizamos localmente para feedback imediato e reconciliamos depois.
  const patchReminderLocally = (reminderId, patch) => {
    setData(prev => {
      if (!prev) return prev;
      const nextUsers = prev.users.map(u => {
        const idx = (u.reminders || []).findIndex(r => r.id === reminderId);
        if (idx === -1) return u;
        const updated = { ...u.reminders[idx], ...patch };
        const reminders = [...u.reminders];
        reminders[idx] = updated;
        // Recalcula contadores ativos/inativos
        const active = reminders.filter(r => r.active).length;
        return { ...u, reminders, reminders_active: active, reminders_inactive: reminders.length - active };
      });
      return { ...prev, users: nextUsers };
    });
  };

  const handleDeactivateReminder = async (reminder) => {
    if (!window.confirm(`Desativar o lembrete '${reminder.template_name}' às ${reminder.schedule_time}?`)) return;
    setActingOn(reminder.id);
    // Patch local imediato — feedback instantâneo, reconcilia no próximo load
    patchReminderLocally(reminder.id, { active: false });
    try {
      await reminderService.deactivate(reminder.id);
      flashSuccess('Lembrete desativado.');
      // Reload em background — reconcilia totais (totals) e logs sem bloquear a UI
      load();
    } catch {
      // Rollback se falhou
      patchReminderLocally(reminder.id, { active: true });
      setError('Erro ao desativar lembrete.');
    } finally {
      setActingOn(null);
    }
  };

  const handleSendNow = async (reminder) => {
    setActingOn(reminder.id);
    try {
      const res = await reminderService.sendNow(reminder.id);
      if (res.success) flashSuccess(`Enviado (${res.delivery_mode}).`);
      else setError(`Falha: ${res.error || 'desconhecido'}`);
    } catch {
      setError('Erro ao enviar lembrete.');
    } finally {
      setActingOn(null);
    }
  };

  const handleBulkDeactivate = async (phone) => {
    if (!window.confirm(`Bloquear TODOS os lembretes deste usuário (${phone})?\n\nIsso desativa todos os lembretes ativos. Pode ser revertido criando-os novamente.`)) return;
    // Patch local: marca todos os reminders ativos do usuário como inativos
    setData(prev => {
      if (!prev) return prev;
      const nextUsers = prev.users.map(u => {
        if (u.phone !== phone) return u;
        const reminders = (u.reminders || []).map(r => r.active ? { ...r, active: false } : r);
        return { ...u, reminders, reminders_active: 0, reminders_inactive: reminders.length };
      });
      return { ...prev, users: nextUsers };
    });
    try {
      const res = await drSalusService.bulkDeactivateUserReminders(phone);
      flashSuccess(`${res.deactivated} lembrete(s) desativado(s).`);
      load();
    } catch {
      setError('Erro ao bloquear lembretes do usuário.');
      load();
    }
  };

  const openEngagement = (user) => {
    setEngagementForm({
      phone: user.phone,
      max_consecutive_unanswered: user.engagement.max_consecutive_unanswered ?? '',
      reengagement_template_name: user.engagement.reengagement_template_name || '',
    });
  };

  const handleSaveEngagement = async () => {
    if (!engagementForm) return;
    setSavingEng(true);
    try {
      const payload = {};
      const v = engagementForm.max_consecutive_unanswered;
      if (v !== '' && v !== null && v !== undefined) {
        payload.max_consecutive_unanswered = parseInt(v, 10);
      }
      payload.reengagement_template_name = engagementForm.reengagement_template_name || '';
      await drSalusService.updateEngagement(engagementForm.phone, payload);
      flashSuccess('Engajamento salvo.');
      setEngagementForm(null);
      await load();
    } catch {
      setError('Erro ao salvar engajamento.');
    } finally {
      setSavingEng(false);
    }
  };

  const handleResume = async (phone) => {
    try {
      await drSalusService.resumeReminders(phone);
      flashSuccess('Lembretes reativados.');
      await load();
    } catch {
      setError('Erro ao reativar.');
    }
  };

  const handleCleanDuplicates = async (phone = null) => {
    try {
      // Dry-run primeiro para mostrar quantos duplicados existem
      const preview = await drSalusService.cleanDuplicateReminders({ phone, dryRun: true });
      const total = preview.total_extras || 0;
      if (total === 0) {
        flashSuccess(phone ? 'Nenhum duplicado encontrado para este usuário.' : 'Nenhum duplicado encontrado.');
        return;
      }
      const scope = phone ? `do usuário ${phone}` : 'em todos os usuários';
      const confirmed = window.confirm(
        `Encontrei ${total} lembrete(s) duplicado(s) ${scope}.\n\n` +
        `Definição: mesmo telefone + template + horário + tipo (ativo).\n` +
        `Mantém o mais antigo de cada grupo, desativa os demais.\n\n` +
        `Deseja prosseguir com a limpeza?`
      );
      if (!confirmed) return;
      const result = await drSalusService.cleanDuplicateReminders({ phone, dryRun: false });
      flashSuccess(`${result.deactivated_count} duplicado(s) desativado(s).`);
      await load();
    } catch {
      setError('Erro ao limpar duplicados.');
    }
  };

  const s = {
    // mainArea do Inbox usa overflow:hidden — a página precisa rolar dentro do
    // próprio container, e largura total para não ficar apertada à esquerda.
    page: {
      padding: '24px 28px 48px',
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      overflowY: 'auto',
      boxSizing: 'border-box',
    },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
    title: { fontSize: '20px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '10px' },
    btn: (variant = 'primary') => ({
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      backgroundColor: variant === 'primary' ? '#10b981' : (variant === 'danger' ? '#ef444420' : colors.bgSecondary),
      color: variant === 'primary' ? '#fff' : (variant === 'danger' ? '#ef4444' : colors.textPrimary),
    }),
    summary: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px', marginBottom: '20px',
    },
    kpi: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '12px', padding: '14px 18px',
    },
    kpiLabel: { fontSize: '11px', textTransform: 'uppercase', color: colors.textSecondary, letterSpacing: '0.5px' },
    kpiValue: { fontSize: '22px', fontWeight: 700, color: colors.textPrimary, marginTop: '4px' },
    filters: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' },
    searchBox: {
      flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
    },
    searchInput: { flex: 1, border: 'none', backgroundColor: 'transparent', color: colors.textPrimary, fontSize: '13px', outline: 'none' },
    checkLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: colors.textSecondary, cursor: 'pointer' },
    card: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '12px', marginBottom: '8px', overflow: 'hidden',
    },
    cardHeader: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
      cursor: 'pointer', userSelect: 'none',
    },
    badge: (bg, c) => ({ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: bg, color: c }),
    cardBody: { padding: '0 16px 16px', borderTop: `1px solid ${colors.borderColor}` },
    reminderRow: {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 0', borderBottom: `1px solid ${colors.borderColor}`,
    },
    label: { fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '6px', display: 'block' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '14px', boxSizing: 'border-box' },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalBox: { backgroundColor: colors.bgPrimary, borderRadius: '16px', padding: '28px', width: '480px', maxWidth: '95vw' },
    iconBtn: { padding: '6px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent', color: colors.textSecondary },
  };

  const users = data?.users || [];
  const totals = data?.totals || {};

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}><Activity size={22} color="#10b981" /> Painel de Controle — Dr. Salus</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.btn('secondary')} onClick={() => handleCleanDuplicates(null)} title="Detectar e desativar lembretes duplicados em todos os usuários">
            <CopyIcon size={14} /> Limpar duplicados
          </button>
          <button style={s.btn('secondary')} onClick={() => load()} disabled={refreshing}>
            {refreshing ? <Loader2 size={14} /> : <RefreshCw size={14} />} Atualizar
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{ padding: '10px 16px', backgroundColor: '#10b98120', color: '#10b981', borderRadius: '8px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={16} /> {successMsg}
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 16px', backgroundColor: '#ef444420', color: '#ef4444', borderRadius: '8px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div style={s.summary}>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>Usuários ativos</div>
          <div style={s.kpiValue}>{users.length}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>Lembretes ativos</div>
          <div style={s.kpiValue}>{totals.active_reminders ?? 0}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>Pausados</div>
          <div style={{ ...s.kpiValue, color: (totals.paused_users || 0) > 0 ? '#f59e0b' : colors.textPrimary }}>
            {totals.paused_users ?? 0}
          </div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>Envios (7d)</div>
          <div style={s.kpiValue}>{totals.sent_7d ?? 0}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>Falhas (7d)</div>
          <div style={{ ...s.kpiValue, color: (totals.failed_7d || 0) > 0 ? '#ef4444' : colors.textPrimary }}>
            {totals.failed_7d ?? 0}
          </div>
        </div>
      </div>

      <form onSubmit={handleSearch} style={s.filters}>
        <div style={s.searchBox}>
          <Search size={14} color={colors.textSecondary} />
          <input
            style={s.searchInput}
            placeholder="Buscar por telefone (parcial)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
          Incluir inativos
        </label>
        <button type="submit" style={s.btn('secondary')}>Filtrar</button>
      </form>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={24} color="#10b981" />
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: colors.textSecondary }}>
          Nenhum usuário com lembretes ativos.
        </div>
      ) : users.map(user => {
        const isOpen = expandedPhone === user.phone;
        const isPaused = user.engagement.is_paused;
        const unanswered = user.engagement.consecutive_unanswered_templates || 0;
        const cap = user.engagement.max_consecutive_unanswered || 5;
        const failed7d = user.activity_7d.failed_count || 0;

        return (
          <div key={user.phone} style={s.card}>
            <div style={s.cardHeader} onClick={() => setExpandedPhone(isOpen ? null : user.phone)}>
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {user.contact_name || user.phone}
                  <span style={{ color: colors.textSecondary, fontSize: '12px', fontWeight: 400 }}>{user.phone}</span>
                  <span style={s.badge('#10b98120', '#10b981')}>
                    <Bell size={10} style={{ display: 'inline', marginRight: '4px' }} />
                    {user.reminders_active} ativo{user.reminders_active === 1 ? '' : 's'}
                  </span>
                  {user.reminders_inactive > 0 && (
                    <span style={s.badge('#6b728020', '#6b7280')}>{user.reminders_inactive} inativo{user.reminders_inactive === 1 ? '' : 's'}</span>
                  )}
                  {isPaused ? (
                    <span style={s.badge('#f59e0b20', '#f59e0b')}>
                      <Pause size={10} style={{ display: 'inline', marginRight: '4px' }} />
                      Pausado até {new Date(user.engagement.templates_paused_until).toLocaleDateString('pt-BR')}
                    </span>
                  ) : unanswered > 0 ? (
                    <span style={s.badge('#3b82f620', '#3b82f6')}>
                      <BellOff size={10} style={{ display: 'inline', marginRight: '4px' }} />
                      {unanswered}/{cap} sem resposta
                    </span>
                  ) : (
                    <span style={s.badge('#22c55e20', '#22c55e')}>
                      <CheckCircle2 size={10} style={{ display: 'inline', marginRight: '4px' }} />
                      engajado
                    </span>
                  )}
                  {failed7d > 0 && (
                    <span style={s.badge('#ef444420', '#ef4444')}>
                      <AlertTriangle size={10} style={{ display: 'inline', marginRight: '4px' }} />
                      {failed7d} falha{failed7d === 1 ? '' : 's'} (7d)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '4px' }}>
                  Último envio: {formatRelative(user.engagement.last_template_at || user.activity_7d.last_sent_at)}
                  {user.activity_7d.last_template_name && ` · ${user.activity_7d.last_template_name} (${user.activity_7d.last_delivery_mode || '?'})`}
                  {user.engagement.last_user_reply_at && ` · resposta do usuário ${formatRelative(user.engagement.last_user_reply_at)}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                <button style={s.btn('secondary')} onClick={() => handleCleanDuplicates(user.phone)} title="Detectar e remover lembretes duplicados deste usuário">
                  <CopyIcon size={13} /> Duplicados
                </button>
                <button style={s.btn('secondary')} onClick={() => openEngagement(user)} title="Gerenciar engajamento">
                  <Bell size={13} /> Engajamento
                </button>
                {isPaused && (
                  <button style={{ ...s.btn('secondary'), color: '#10b981' }} onClick={() => handleResume(user.phone)} title="Reativar lembretes">
                    <Power size={13} /> Reativar
                  </button>
                )}
                {user.reminders_active > 0 && (
                  <button style={s.btn('danger')} onClick={() => handleBulkDeactivate(user.phone)} title="Bloquear todos os lembretes">
                    <Trash2 size={13} /> Bloquear
                  </button>
                )}
              </div>
            </div>

            {isOpen && (
              <div style={s.cardBody}>
                {user.reminders.length === 0 ? (
                  <div style={{ padding: '12px 0', color: colors.textSecondary, fontSize: '12px' }}>
                    Sem lembretes para este usuário (apenas atividade recente em template_logs).
                  </div>
                ) : user.reminders.map(r => (
                  <div key={r.id} style={{ ...s.reminderRow, opacity: r.active ? 1 : 0.5 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                        {r.template_name}
                        {!r.active && <span style={{ ...s.badge('#6b728020', '#6b7280'), marginLeft: '8px' }}>desativado</span>}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                        {formatSchedule(r)}
                        {r.last_sent_date && ` · último envio ${r.last_sent_date}`}
                        {Object.keys(r.template_variables || {}).length > 0 &&
                          ` · vars: ${Object.entries(r.template_variables).map(([k, v]) => `${k}=${v}`).join(', ')}`}
                      </div>
                    </div>
                    <button style={s.iconBtn} disabled={actingOn === r.id} onClick={() => handleSendNow(r)} title="Testar agora">
                      {actingOn === r.id ? <Loader2 size={14} /> : <Send size={14} />}
                    </button>
                    {r.active && (
                      <button style={s.iconBtn} disabled={actingOn === r.id} onClick={() => handleDeactivateReminder(r)} title="Desativar">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {user.activity_7d.sent_count > 0 && (
                  <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: colors.bgPrimary, borderRadius: '8px', fontSize: '11px', color: colors.textSecondary }}>
                    Últimos 7 dias: <strong style={{ color: colors.textPrimary }}>{user.activity_7d.sent_count}</strong> envio{user.activity_7d.sent_count === 1 ? '' : 's'} · <strong style={{ color: failed7d > 0 ? '#ef4444' : colors.textPrimary }}>{user.activity_7d.failed_count}</strong> falha{user.activity_7d.failed_count === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {engagementForm && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bell size={16} color="#10b981" /> Engajamento — {engagementForm.phone}
              </h2>
              <button style={s.iconBtn} onClick={() => setEngagementForm(null)}><X size={18} /></button>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={s.label}>
                Máximo de templates sem resposta antes de pausar
                {engagementSettings && <span style={{ color: colors.textSecondary, fontWeight: 400 }}> (default: {engagementSettings.default_max_consecutive_unanswered})</span>}
              </label>
              <input
                style={s.input}
                type="number"
                min="1"
                max="100"
                placeholder={`${engagementSettings?.default_max_consecutive_unanswered ?? 5}`}
                value={engagementForm.max_consecutive_unanswered}
                onChange={e => setEngagementForm(f => ({ ...f, max_consecutive_unanswered: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={s.label}>Template de reengajamento (enviado UMA vez ao bater o cap)</label>
              <select
                style={{ ...s.input, cursor: 'pointer' }}
                value={engagementForm.reengagement_template_name}
                onChange={e => setEngagementForm(f => ({ ...f, reengagement_template_name: e.target.value }))}
              >
                <option value="">— Nenhum (apenas pausar) —</option>
                {approvedTemplates.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
              {engagementSettings && (
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '6px' }}>
                  Pausa de {engagementSettings.default_pause_days} dias após o cap. Resposta do usuário reativa automaticamente.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={s.btn('secondary')} onClick={() => setEngagementForm(null)} disabled={savingEng}>Cancelar</button>
              <button style={s.btn('primary')} onClick={handleSaveEngagement} disabled={savingEng}>
                {savingEng ? <Loader2 size={14} /> : <Check size={14} />} Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
